import React, { useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Theme } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from './Button';
import TextInput from './TextInput';
import Form from './Form';
import { StyledText } from './StyledText';
import { FilterOption } from '../types/types';
import Checkbox from './Checkbox';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: 800
  },
  dialogAction: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

interface FilterModalProps {
  open: boolean
  filterOptions: FilterOption[]
  onClose: () => void
  onSubmit: (v: FilterOption[]) => void
}

const NetworkFilterModal: React.FC<FilterModalProps> = ({ open, filterOptions, onClose, onSubmit }) => {
  const styles = useStyles();

  const initialValues = useMemo(() => ({
    contactName: filterOptions.find((a) => a.key === 'contactName')?.value || '',
    companyName: filterOptions.find((a) => a.key === 'companyName')?.value || '',
    publishedOnly: filterOptions.find((a) => a.key === 'publishedOnly')?.value || false
  }), [filterOptions]);

  const handleSubmit = (values: { contactName?: string, companyName?: string, publishedOnly?: boolean }) => {
    const { contactName, companyName, publishedOnly } = values;
    const filterOpts: FilterOption[] = [];
    if (contactName) {
      filterOpts.push({
        key: 'contactName',
        value: contactName,
        query: `contactName=${contactName}`,
        label: `Investor Name: ${contactName}`
      });
    }
    if (companyName) {
      filterOpts.push({
        key: 'companyName',
        value: companyName,
        query: `companyName=${companyName}`,
        label: `Company: ${companyName}`
      });
    }
    if (publishedOnly) {
      filterOpts.push({
        key: 'publishedOnly',
        value: publishedOnly,
        query: `publishedOnly=${publishedOnly}`,
        label: `Published only: ${publishedOnly}`
      });
    }
    onSubmit(filterOpts);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      contactName: Yup.string().optional().nullable(),
      companyName: Yup.string().optional().nullable(),
      publishedOnly: Yup.boolean().optional().nullable()
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit
  });

  return (
    <Dialog open={open} onClose={onClose} className={styles.root}>
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Filters</StyledText>
        </DialogTitle>
        <DialogContent>
          <TextInput
            id='contactName'
            label='Contact Name'
            type='text'
            placeholder=''
            fullWidth
          />
          <TextInput
            id='companyName'
            label='Company Name'
            type='text'
            placeholder=''
            fullWidth
          />
          <div style={{ marginTop: '10px' }}>
            <Checkbox
              id='publishedOnly'
              checked={formik.values.publishedOnly || false}
              label='Only show published'
              onChange={(val: any) => {
                formik.setFieldValue('publishedOnly', !(formik.values.publishedOnly || false));
              }}
              onBlur={() => {}}
            />
          </div>
        </DialogContent>
        <DialogActions className={styles.dialogAction}>
          <Button type='button' variant='text' onClick={onClose}>Close</Button>
          <Button type='submit'>Filter</Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default NetworkFilterModal;
